<template>
  <div class="mask" @mousedown.self="onClose">
    <div class="rename-dialog">
      <!-- ヘッダー -->
      <div class="rename-header">
        <span class="rename-header-title">{{ $t("homePage.novelList.grouping.renameTitle") }}</span>
        <button class="rename-close-button" @click="onClose">
          <IconClose class="rename-close-icon" />
        </button>
      </div>

      <div class="rename-body">
        <div class="rename-label-row">
          <span class="rename-label">{{ $t("homePage.novelList.grouping.groupName") }}</span>
          <span class="rename-char-count" :class="{ over: isOverLimit }">{{ charCount }}/50</span>
        </div>
        <input
          v-model="groupName"
          class="rename-input"
          type="text"
          :placeholder="$t('homePage.novelList.grouping.groupNamePlaceholder')"
          :maxlength="GROUP_NAME_MAX_LENGTH"
        />
      </div>

      <div class="rename-actions">
        <button
          class="rename-submit-button"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          {{ $t("homePage.novelList.grouping.update") }}
        </button>
        <button class="rename-cancel-button" @click="onClose">
          {{ $t("common.close") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import IconClose from "~icons/figma/Close";

const GROUP_NAME_MAX_LENGTH = 50;

export interface NovelGroupRenameDialogProps {
  groupId: string;
  currentName: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { IconClose },
  props: {
    groupId: {
      type: String,
      required: true,
    },
    currentName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      groupName: this.currentName,
    };
  },

  computed: {
    GROUP_NAME_MAX_LENGTH() {
      return GROUP_NAME_MAX_LENGTH;
    },
    charCount() {
      return this.groupName.length;
    },
    isOverLimit() {
      return this.groupName.length > GROUP_NAME_MAX_LENGTH;
    },
    canSubmit() {
      const trimmed = this.groupName.trim();
      return trimmed.length > 0 && !this.isOverLimit && trimmed !== this.currentName;
    },
  },

  methods: {
    async onSubmit() {
      if (!this.canSubmit) return;

      try {
        await this.$store.dispatch("novelGroupModule/updateGroup", {
          groupId: this.groupId,
          name: this.groupName.trim(),
        });
        this.$close(true);
      } catch (e) {
        const errorDialog = new Dialog(SimpleDialog);
        const errorData: SimpleDialogProps = {
          title: this.$t("homePage.novelList.grouping.errorTitle") as string,
          content: this.$t("homePage.novelList.grouping.errorMessage") as string,
          isError: true,
          width: 350,
        };
        errorDialog.show(errorData);
      }
    },

    onClose() {
      this.$close(null);
    },
  },
});

interface Props {
  groupId: string;
  currentName: string;
}

interface Data {
  groupName: string;
}

interface Computed {
  GROUP_NAME_MAX_LENGTH: number;
  charCount: number;
  isOverLimit: boolean;
  canSubmit: boolean;
}

interface Methods {
  onSubmit(): Promise<void>;
  onClose(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 8002;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.rename-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
}

.rename-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.rename-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.rename-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.rename-close-icon {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.rename-body {
  padding: 8px 24px 0;
}

.rename-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.rename-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.rename-char-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-low);

  &.over {
    color: var(--color-warning);
  }
}

.rename-input {
  width: 100%;
  padding: 10px 12px;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-sm);
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-inactive);
  }
}

.rename-actions {
  padding: 12px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rename-submit-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-primary);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.rename-cancel-button {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
